import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Radio, Select, Switch, TextInput } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_CARRIER_VERIFICATION_ROUTING } from 'src/graphql/carriers.graphql'
import { VOE_PROVIDER_OPTIONS } from 'src/lib/voeProvider.utils'

import type { CarrierDetailRecord } from '../carrier.types'

/**
 * The Verification Routing section, in edit mode.
 *
 * Only this section swaps, the same way {@link CarrierEditForm} does for the
 * details section. The list submitted here replaces the stored set — a row
 * taken out of the form is deleted on save.
 */
export interface VerificationRoutingEditFormProps {
  carrier: CarrierDetailRecord
  onClose: () => void
  /** Re-reads the carrier after a save; the Apollo cache does not merge Carrier. */
  onSaved: () => void
}

/**
 * One platform row while it is being edited. The provider is null until the
 * admin picks one, which is the one thing the server will not accept.
 */
type PlatformRow = {
  /** Stable across re-renders so React does not re-mount an input mid-typing. */
  key: string
  provider: string | null
  notes: string
  preferred: boolean
}

let nextRowKey = 0
const newRow = (preferred: boolean): PlatformRow => ({
  key: `new-${nextRowKey++}`,
  provider: null,
  notes: '',
  preferred,
})

/**
 * Keeps one row preferred whenever there are rows at all. A radio cannot be
 * unchecked, so a list with no preferred row could not be fixed from the form;
 * and when platformOnly is on the server would elect the first row anyway, so
 * doing it here shows the admin what will be saved.
 */
const withPreferred = (rows: PlatformRow[]): PlatformRow[] =>
  rows.length === 0 || rows.some((row) => row.preferred)
    ? rows
    : rows.map((row, index) => ({ ...row, preferred: index === 0 }))

const VerificationRoutingEditForm = ({
  carrier,
  onClose,
  onSaved,
}: VerificationRoutingEditFormProps) => {
  const [platformOnly, setPlatformOnly] = useState(carrier.platformOnly)
  const [rows, setRows] = useState<PlatformRow[]>(() =>
    carrier.verificationPlatforms.map((platform) => ({
      key:
        platform.carrierVerificationPlatformId ??
        `existing-${platform.provider}`,
      provider: platform.provider,
      notes: platform.notes ?? '',
      preferred: platform.preferred,
    }))
  )
  // Errors are held back until the first save attempt so a freshly added row
  // is not red before the admin has had a chance to fill it in.
  const [attempted, setAttempted] = useState(false)

  const [updateRouting, { loading }] = useMutation(
    UPDATE_CARRIER_VERIFICATION_ROUTING,
    {
      onCompleted: () => {
        toast('Verification routing updated', 'success')
        onSaved()
        onClose()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const updateRow = (key: string, patch: Partial<PlatformRow>) =>
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row))
    )

  const prefer = (key: string) =>
    setRows((current) =>
      current.map((row) => ({ ...row, preferred: row.key === key }))
    )

  const removeRow = (key: string) =>
    setRows((current) =>
      withPreferred(current.filter((row) => row.key !== key))
    )

  const addRow = () =>
    setRows((current) => [...current, newRow(current.length === 0)])

  const missingProvider = rows.some((row) => !row.provider)
  const noPlatformError =
    platformOnly && rows.length === 0
      ? 'A platform-only carrier needs at least one platform to send recruiters to.'
      : null

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setAttempted(true)
    if (missingProvider || noPlatformError) return

    updateRouting({
      variables: {
        carrierId: carrier.carrierId,
        input: {
          platformOnly,
          platforms: rows.map((row) => ({
            provider: row.provider,
            notes: row.notes.trim() || null,
            preferred: row.preferred,
          })),
        },
      },
    })
  }

  return (
    <form onSubmit={submit}>
      <DetailSection
        title="Verification Routing"
        description="The platforms listed here replace what is stored: take a row out and it is deleted on save. Each platform can appear once, and one of them is marked preferred so recruiters know where to go."
        columns={1}
        actions={
          <div className="flex items-center gap-2">
            <DNButton variant="transparent" onClick={onClose} type="button">
              Cancel
            </DNButton>
            <DNButton
              variant="primary"
              type="submit"
              loading={loading}
              disabled={noPlatformError !== null}
            >
              Save
            </DNButton>
          </div>
        }
      >
        <Switch
          id="routing-platform-only"
          label="Platform only — do not contact this carrier directly"
          description="Stops automated email outreach to this carrier; recruiters are pointed at the preferred platform and can still email or call by hand."
          checked={platformOnly}
          onChange={(event) => setPlatformOnly(event.currentTarget.checked)}
        />

        <div className="flex flex-col gap-3">
          {rows.length === 0 && (
            <div className="text-sm italic text-doubleNickel-gray-400">
              No platforms recorded
            </div>
          )}

          {rows.map((row, index) => {
            // A provider may be chosen once, so a row offers only the providers
            // no sibling has taken — plus its own, or its current pick would
            // vanish from its own dropdown.
            const takenElsewhere = new Set(
              rows
                .filter((other) => other.key !== row.key)
                .map((other) => other.provider)
            )
            const options = VOE_PROVIDER_OPTIONS.filter(
              (option) => !takenElsewhere.has(option.value)
            )

            return (
              <div
                key={row.key}
                className="grid grid-cols-1 items-start gap-3 rounded-md border border-doubleNickel-gray-200 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto_auto]"
              >
                <Select
                  id={`routing-platform-${index}-provider`}
                  label="Platform"
                  placeholder="Choose a platform"
                  data={options}
                  value={row.provider}
                  onChange={(value) => updateRow(row.key, { provider: value })}
                  allowDeselect={false}
                  error={
                    attempted && !row.provider ? 'Choose a platform' : undefined
                  }
                />
                <TextInput
                  id={`routing-platform-${index}-notes`}
                  label="Notes"
                  placeholder="e.g. Use the employer portal; responses take 2 days"
                  value={row.notes}
                  onChange={(event) =>
                    updateRow(row.key, { notes: event.currentTarget.value })
                  }
                />
                {/* One name across the rows makes the browser treat them as a
                    group, so checking one unchecks the rest for free. */}
                <Radio
                  id={`routing-platform-${index}-preferred`}
                  name="routing-preferred-platform"
                  label="Preferred"
                  value={row.key}
                  checked={row.preferred}
                  onChange={() => prefer(row.key)}
                  className="sm:pt-7"
                />
                <DNButton
                  variant="transparent"
                  type="button"
                  onClick={() => removeRow(row.key)}
                  className="text-doubleNickel-error-600 sm:mt-6"
                  aria-label={`Remove platform ${index + 1}`}
                >
                  Remove
                </DNButton>
              </div>
            )
          })}

          {noPlatformError && (
            <div role="alert" className="text-sm text-doubleNickel-error-600">
              {noPlatformError}
            </div>
          )}

          <div>
            <DNButton variant="outline" type="button" onClick={addRow}>
              Add platform
            </DNButton>
          </div>
        </div>
      </DetailSection>
    </form>
  )
}

export default VerificationRoutingEditForm
